import Badge, { BadgeColor } from 'lib/components/badge/badge'
import { Popover, PopoverContent, PopoverTrigger } from 'lib/components/popover/popover'
import { CheckIcon, XCircleIcon } from 'lucide-react'
import { cn } from 'lib/util/cn'
import { ProjectStatus } from 'interfaces/project'
import Button from 'components/core/button'
import IconButton from 'lib/components/buttons/icon-button'
import { capitalize } from 'lodash'

interface ProjectStatusPopoverProps {
  status: ProjectStatus
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  setStatus: React.Dispatch<React.SetStateAction<ProjectStatus>>
}

export function getBadgeColor(status: ProjectStatus) {
  switch (status) {
    case ProjectStatus.ToDo:
      return 'neutral'
    case ProjectStatus.InProgress:
      return 'primary'
    case ProjectStatus.Done:
      return 'success'
    default:
      return 'light'
  }
}

export function getBadgeLabel(status: ProjectStatus) {
  switch (status) {
    case ProjectStatus.ToDo:
      return 'To Do'
    case ProjectStatus.InProgress:
      return 'In Progress'
    case ProjectStatus.Done:
      return 'Done'
    default:
      return 'No Status'
  }
}

export default function ProjectStatusPopover({ status, open, setOpen, setStatus }: ProjectStatusPopoverProps) {
  const displayStatus =
    status === ProjectStatus.NoStatus
      ? '+ Add status'
      : status
          ?.split('_')
          .map((word) => capitalize(word))
          .join(' ')
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="tw-flex tw-items-center tw-gap-1">
        <Badge color={getBadgeColor(status)} style="pill" className="tw-transition-all hover:tw-shadow-lg">
          <PopoverTrigger asChild>
            <Button
              color="unstyled"
              onClick={() => setOpen((prev) => !prev)}
              className="tw-p-0 tw-pl-1 tw-text-sm tw-text-inherit"
            >
              {displayStatus}
            </Button>
          </PopoverTrigger>
          {status !== ProjectStatus.NoStatus && (
            <IconButton
              color="transparent"
              size="xs"
              onClick={() => setStatus(ProjectStatus.NoStatus)}
              className="hover:tw-bg-transparent hover:tw-shadow-none active:tw-bg-transparent active:tw-shadow-none"
            >
              <XCircleIcon className="tw-h-4 tw-w-4" />
            </IconButton>
          )}
        </Badge>
      </div>
      <PopoverContent className="tw-bg-white">
        <h5>Status</h5>
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-2">
          <StatusOption
            isSelected={status === ProjectStatus.ToDo}
            onClick={() => setStatus(ProjectStatus.ToDo)}
            badgeColor="neutral"
            label="To Do"
          />
          <StatusOption
            isSelected={status === ProjectStatus.InProgress}
            onClick={() => setStatus(ProjectStatus.InProgress)}
            badgeColor="primary"
            label="In Progress"
          />
          <StatusOption
            isSelected={status === ProjectStatus.Done}
            onClick={() => setStatus(ProjectStatus.Done)}
            badgeColor="success"
            label="Done"
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface StatusOptionProps {
  isSelected: boolean
  onClick: () => void
  badgeColor: BadgeColor
  label: string
}

function StatusOption({ isSelected, onClick, badgeColor, label }: StatusOptionProps) {
  return (
    <div
      className={cn('tw-w-full tw-rounded-md tw-pr-2', {
        'tw-bg-neutral-100': isSelected,
      })}
    >
      <Button color="unstyled" onClick={onClick} className="tw-px-1 tw-transition-all">
        <Badge
          color={badgeColor}
          style="pill"
          className="tw-px-4 tw-py-2 tw-text-sm tw-font-normal tw-transition-all hover:tw-shadow-lg"
        >
          {label}
        </Badge>
      </Button>
      {isSelected && <CheckIcon className="tw-text-neutral-500" />}
    </div>
  )
}
